import { useState } from 'react';
import { complianceApi } from '@/api/compliance.api';

interface OtpAuthModalProps {
  onClose: () => void;
  onAuthenticated?: (expiresAt: string | null) => void;
}

/**
 * Two-step OTP authentication against Whitebooks' GST Return Filing API (PDF §2):
 * (1) request an OTP to the GST-registered mobile/email, (2) enter the OTP to
 * exchange it for a 6-hour session token. Shared by TDS/TCS Settings and the
 * Quarterly Return page's "Authenticate with GSTN" step.
 */
export default function OtpAuthModal({ onClose, onAuthenticated }: OtpAuthModalProps) {
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const handleSendOtp = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await complianceApi.requestGstnOtp();
      if (res?.success) {
        setStep('verify');
        setInfo(res.message || 'OTP sent to your GST-registered mobile/email');
      } else {
        setError(res?.message || 'Failed to send OTP');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim() || otp.trim().length < 4) {
      setError('Enter the OTP you received');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await complianceApi.verifyGstnOtp(otp.trim());
      if (res?.success) {
        setExpiresAt(res.expiresAt || null);
        setInfo(null);
        onAuthenticated?.(res.expiresAt || null);
      } else {
        setError(res?.message || 'OTP verification failed');
      }
    } catch (err: any) {
      setError(err?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in-95 duration-150 border border-slate-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="w-10 h-10 rounded-full bg-indigo-50 text-[#4f46e5] flex items-center justify-center mb-3">
          <i className="ri-shield-keyhole-line text-xl" />
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-0.5">Authenticate with GSTN</h3>
        <p className="text-xs text-gray-500 mb-4">
          {expiresAt
            ? 'Session active.'
            : step === 'request'
            ? "We'll send an OTP to your GST-registered mobile/email."
            : 'Enter the OTP you received to complete authentication.'}
        </p>

        {expiresAt ? (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 flex items-start gap-2 mb-4">
            <i className="ri-checkbox-circle-line text-sm shrink-0 mt-0.5" />
            <span>Authenticated ✓ Session valid until {new Date(expiresAt).toLocaleString('en-IN')}</span>
          </div>
        ) : step === 'request' ? (
          <button
            onClick={handleSendOtp}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer mb-4"
          >
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
            Send OTP
          </button>
        ) : (
          <div className="space-y-3 mb-4">
            {info && (
              <div className="p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700">{info}</div>
            )}
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
              maxLength={8}
              placeholder="Enter OTP"
              autoFocus
              className="w-full px-3 py-2.5 text-center text-lg tracking-[0.3em] font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.trim().length < 4}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
                Verify
              </button>
              <button
                onClick={handleSendOtp}
                disabled={loading}
                className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Resend
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 mb-2">
            <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-2 w-full py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
        >
          {expiresAt ? 'Close' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
